<!-- app-subdomain-creators\src\accountPages\CreatorAppSettings.vue -->

<template>
  <div class="w-full">
    <!-- Page Header -->
    <!-- <div class="px-6 lg:px-8 pt-6">
      <h2 class="text-2xl font-semibold text-zinc-800 mb-6">
        Membership App Settings
      </h2>
    </div> -->

    <!-- Tabs Navigation -->
    <div class="">
      <nav
        class="flex px-6 space-x-8"
        aria-label="Tabs"
      >
        <router-link
          v-for="tab in visibleTabs"
          :key="tab.name"
          :to="tab.to"
          class="whitespace-nowrap py-4 px-1 border-b-2 text-sm font-medium"
          :class="tab.current
            ? 'border-[#2D8CFF] text-[#2D8CFF]'
            : 'border-transparent text-zinc-500 hover:text-zinc-700 hover:border-zinc-300'"
          @click="setActiveTab(tab.name)"
        >
          {{ tab.name }}
        </router-link>
      </nav>
    </div>

    <!-- Tab Content -->
    <div class="p-6">
      <router-view v-slot="{ Component, route: tabRoute }">
        <transition name="settings-tab" mode="out-in">
          <div :key="tabRoute.name || tabRoute.path">
            <component :is="Component" />
          </div>
        </transition>
      </router-view>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { fetchCreatorStaffSession, hasPermissionPath } from '@/utils/creatorStaffAccess';

const route = useRoute();
const router = useRouter();
const tabs = ref([
  { name: 'Domains', to: '/creator/app-settings/domains', current: false },
  { name: 'Integrations & API', to: '/creator/app-settings/integrations', current: false },
  { name: 'Plan Tiers', to: '/creator/app-settings/plan-tiers', current: false },
  { name: 'Access Controls', to: '/creator/app-settings/access-controls', current: false },
  { name: 'Emails', to: '/creator/app-settings/emails', current: false },
  { name: 'Conversion Tracking', to: '/creator/app-settings/conversion-tracking', current: false },
  { name: 'Crypto', to: '/creator/app-settings/crypto', current: false },
  { name: 'Affiliates', to: '/creator/app-settings/affiliates', current: false },
]);
const staffSession = ref(null);
const visibleTabs = computed(() => {
  const session = staffSession.value;
  if (!session?.isStaff || String(session.role || '').toLowerCase() === 'admin') {
    return tabs.value;
  }
  return tabs.value.filter((tab) => {
    if (tab.to.endsWith('/domains')) return hasPermissionPath(session.permissions, 'platform.memberSettingsDomains');
    if (tab.to.endsWith('/integrations')) return hasPermissionPath(session.permissions, 'platform.memberSettingsPayments');
    if (tab.to.endsWith('/plan-tiers')) return hasPermissionPath(session.permissions, 'platform.memberSettingsPayments');
    if (tab.to.endsWith('/access-controls')) return hasPermissionPath(session.permissions, 'platform.memberSettingsPayments');
    if (tab.to.endsWith('/emails')) return hasPermissionPath(session.permissions, 'platform.memberSettingsPayments');
    if (tab.to.endsWith('/conversion-tracking')) return hasPermissionPath(session.permissions, 'platform.memberSettingsPayments');
    if (tab.to.endsWith('/crypto')) return hasPermissionPath(session.permissions, 'platform.memberSettingsCrypto');
    if (tab.to.endsWith('/affiliates')) return hasPermissionPath(session.permissions, 'platform.memberSettingsPayments');
    return false;
  });
});

// Update active tab on route change
const updateActiveTab = () => {
  tabs.value.forEach((tab) => {
    tab.current = route.path === tab.to;
  });
};

const setActiveTab = (name) => {
  tabs.value.forEach((tab) => {
    tab.current = tab.name === name;
  });
};

watch(route, updateActiveTab, { immediate: true });

onMounted(async () => {
  try {
    const session = await fetchCreatorStaffSession();
    staffSession.value = session;
    const hasContainerAccess = !session?.isStaff || String(session.role || '').toLowerCase() === 'admin'
      || hasPermissionPath(session.permissions, 'platform.memberSettings');
    if (!hasContainerAccess) {
      router.replace('/creator/dashboard');
      return;
    }
    if (visibleTabs.value.length && !visibleTabs.value.some((tab) => route.path === tab.to)) {
      router.replace(visibleTabs.value[0].to);
    }
  } catch (_error) {
    // do not hard block settings page if session endpoint is temporarily unavailable
  }
});
</script>

<style scoped>
.settings-tab-enter-active,
.settings-tab-leave-active { transition: opacity .16s ease, transform .18s cubic-bezier(.22, 1, .36, 1); }
.settings-tab-enter-from { opacity: 0; transform: translateY(5px); }
.settings-tab-leave-to { opacity: 0; transform: translateY(-2px); }
@media (prefers-reduced-motion: reduce) {
  .settings-tab-enter-active,
  .settings-tab-leave-active { transition: none; }
}
</style>

  
  
